export interface IDefaultAdditionalField {
  title: string;
  defaultValue?: string;
  aliases?: string[];
}

export interface ITemplateFieldConfig {
  /**
   * Template name or keyword to match (case-insensitive substring match).
   * E.g. 'unofy' matches 'Unofy-Invoice', 'Unofy Invoice', etc.
   */
  templateNameKeyword: string;
  defaultFields: IDefaultAdditionalField[];
}

/**
 * Registry of default additional fields per voucher template.
 * Add new template configs or additional fields here to automatically load them on form select.
 */
export const DEFAULT_TEMPLATE_ADDITIONAL_FIELDS: ITemplateFieldConfig[] = [
  {
    templateNameKeyword: 'unofy',
    defaultFields: [
      { title: 'Doctor Name', aliases: ['doctor name', 'doctor'] },
      { title: 'Medicine Days', aliases: ['medicine days'] },
    ],
  },
];

/**
 * Applies default additional fields for a given voucher template name onto the invoice additionalFields object.
 * If templateName matches a registered template (e.g. 'unofy'), its default fields are added.
 * If templateName does NOT match, any empty default fields from other template configs are cleaned up.
 */
export const applyDefaultTemplateFields = (
  templateName: string,
  existingAdditionalFields: any
) => {
  const addFields = existingAdditionalFields
    ? { ...existingAdditionalFields }
    : { apply: false, fields: [] };

  const fields = addFields.fields ? [...addFields.fields] : [];
  const lowerName = (templateName || '').toLowerCase();

  // Find if current template has a registered config
  const matchedConfig = DEFAULT_TEMPLATE_ADDITIONAL_FIELDS.find((config) =>
    lowerName.includes(config.templateNameKeyword.toLowerCase())
  );

  // Collect all default field titles/aliases across ALL template configs for cleanup check
  const allKnownDefaultTitles = DEFAULT_TEMPLATE_ADDITIONAL_FIELDS.flatMap((config) =>
    config.defaultFields.flatMap((df) => [df.title, ...(df.aliases || [])])
  ).map((t) => t.toLowerCase());

  if (matchedConfig) {
    // Current template DOES have default fields (e.g. Unofy-Invoice)
    addFields.apply = true;
    let nextId = fields.reduce((max: number, f: any) => Math.max(max, f.id || 0), 0);

    for (const defaultField of matchedConfig.defaultFields) {
      const aliases = (defaultField.aliases || [defaultField.title]).map((a) => a.toLowerCase());
      const alreadyExists = fields.some(
        (f: any) => f.title && aliases.includes(f.title.toLowerCase())
      );

      if (!alreadyExists) {
        nextId += 1;
        fields.push({
          id: nextId,
          title: defaultField.title,
          value: defaultField.defaultValue || '',
        });
      }
    }
    addFields.fields = fields;
  } else {
    // Current template does NOT have default fields (e.g. New Invoice-2, Default-Invoice-1)
    // Remove any empty fields whose title matches known default fields
    const remainingFields = fields.filter((f: any) => {
      const isKnownDefault = f.title && allKnownDefaultTitles.includes(f.title.toLowerCase());
      const isEmpty = !f.value || String(f.value).trim() === '';
      // Keep field if it's NOT an empty default field
      return !(isKnownDefault && isEmpty);
    });

    addFields.fields = remainingFields;
    if (remainingFields.length === 0) {
      addFields.apply = false;
    }
  }

  return addFields;
};

